import React from 'react';
import pluralize from 'pluralize';
import lodashFind from 'lodash/fp/find';
import lodashCompact from 'lodash/fp/compact';

import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';

import { makeListingAddressStreetDisplay } from 'common/listingHelpers';

import SrTag, {
  SR_TAG_THEME_GREY_OUTLINE,
  SR_TAG_THEME_ORANGE_OUTLINE,
} from 'client/components/SrTag/SrTag';
import { RENTLINX_LISTING_LEAD_HIGHLIGHT_ARCHIVE_REASON_VALUES } from 'common/rentlinxConstants';

import { LEAD_ARCHIVE_ALL_REASONS_OPTION_ITEMS } from 'common/leadConstants';

import SrQualifiedListItem from 'client/components/SrQualifiedListItem/SrQualifiedListItem';
import SrCard from 'client/components/SrCard/SrCard';
import SrText from 'client/components/SrText';
import SrLink from 'client/components/SrLink/SrLink';
import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrTooltip, {
  SR_TOOLTIP_ICON_QUESTION_MARK,
  SR_TOOLTIP_POSITION_LEFT,
} from 'client/components/SrTooltip';
import SrSeparator from 'client/components/SrSeparator';
import SrListingNotesChip from 'client/components/SrListingNotesChip';

const SrLeadHealthCard = ({ leadHealth, showListingDetails, reloadListingNotes }) => {
  const _getProcessingAppsIndicator = () => {
    const {
      checks: { appInProgress: isQualified } = {},
      households: { active: { count: activeHouseholdCount } = {} } = {},
    } = leadHealth;

    const title = isQualified ? 'Processing Apps' : 'Not Processing Apps';

    const detail = (
      <>
        <SrText font="greycliff" weight="600" fontSize="16px" lineHeight="25px" color="grey">
          {isQualified
            ? `(${activeHouseholdCount} Active ${pluralize('household', activeHouseholdCount)})`
            : '(No active households)'}
        </SrText>
        &nbsp;
        <SrTooltip
          iconName={SR_TOOLTIP_ICON_QUESTION_MARK}
          position={SR_TOOLTIP_POSITION_LEFT}
          className="tooltip-healthIndicator"
        >
          Green checkmark If one or more apps are currently being processed.
        </SrTooltip>
      </>
    );

    return {
      title,
      isQualified,
      detail,
    };
  };

  const _getZillowIndicator = () => {
    const {
      checks: { zillowConnection: isQualified } = {},
      leads: { rentlinx: { zillow: { count, latestFoundAt } = {} } = {} } = {},
    } = leadHealth;

    const title = isQualified ? 'Zillow Connected' : 'Zillow Not Connected';

    const timeDiff = momentWithAustinTimezone(latestFoundAt).fromNow();
    const parsedDateText = latestFoundAt ? `. ${timeDiff} since new lead` : '';

    const detail = (
      <SrText font="greycliff" weight="600" fontSize="16px" lineHeight="25px" color="grey">
        {isQualified
          ? `(${pluralize('leads', count, true)}${parsedDateText})`
          : '(Please check Zillow)'}
      </SrText>
    );

    return {
      title,
      isQualified,
      detail,
    };
  };

  const _getActiveLeadsIndicator = () => {
    const {
      checks: { leads: isQualified },
      leads: {
        archived,
        active: { count: activeCount },
        rentlinx: { latestFoundAt },
      },
    } = leadHealth;

    const title = isQualified ? 'Active Leads' : 'No Active Leads';

    const timeDiff = momentWithAustinTimezone(latestFoundAt).fromNow();
    const parsedDateText = latestFoundAt ? `${timeDiff} since new lead.` : 'No leads found.';

    const archivedCountText = archived.count ? ` ${archived.count} archived.` : '';

    const detail = (
      <SrText font="greycliff" weight="600" fontSize="16px" lineHeight="25px" color="grey">
        {isQualified
          ? `(${activeCount} active ${pluralize(
              'leads',
              activeCount,
            )}, ${parsedDateText}${archivedCountText})` // eslint-disable-line max-len
          : `(${parsedDateText}${archivedCountText})`}
      </SrText>
    );

    return {
      title,
      isQualified,
      detail,
    };
  };

  const _getLeadRatioIndicator = () => {
    const {
      checks,
      leads,
      listing: { daysOnMarket },
    } = leadHealth;

    const title =
      checks.leadRatio === null
        ? 'Lead Ratio Total'
        : checks.leadRatio
        ? 'Good Lead Ratio Total'
        : 'Bad Lead Ratio Total';

    const leadsText = `${pluralize('leads', leads.count, true)}`;

    const detail = (
      <>
        <SrText font="greycliff" weight="600" fontSize="16px" lineHeight="25px" color="grey">
          {`(${leadsText} and ${daysOnMarket} DOM)`}
        </SrText>
        &nbsp;
        <SrTooltip
          iconName={SR_TOOLTIP_ICON_QUESTION_MARK}
          position={SR_TOOLTIP_POSITION_LEFT}
          className="tooltip-healthIndicator"
        >
          Green checkmark if there are at least 5 leads every 7 days.
        </SrTooltip>
      </>
    );

    return {
      title,
      isQualified: checks.leadRatio,
      detail,
    };
  };

  const _getLeadRatioPast14DaysIndicator = () => {
    const { checks, leads } = leadHealth;

    const title =
      checks.leadRatioPast14d === null
        ? 'Lead Ratio Past 14d'
        : checks.leadRatioPast14d
        ? 'Good Lead Ratio Past 14d'
        : 'Bad Lead Ratio Past 14d';

    const leadsText = `${pluralize('leads', leads.past14d.count, true)}`;

    const detail = (
      <>
        <SrText font="greycliff" weight="600" fontSize="16px" lineHeight="25px" color="grey">
          {`(${leadsText} past 14d)`}
        </SrText>
        &nbsp;
        <SrTooltip
          iconName={SR_TOOLTIP_ICON_QUESTION_MARK}
          position={SR_TOOLTIP_POSITION_LEFT}
          className="tooltip-healthIndicator"
        >
          Green checkmark if there are at least 10 leads every 14 days.
        </SrTooltip>
      </>
    );

    return {
      title,
      isQualified: checks.leadRatioPast14d,
      detail,
    };
  };

  const _getTourRatioIndicator = () => {
    const {
      checks: { tourRatio: isQualified } = {},
      leads: { count: leadsCount } = {},
      tours: { count: toursCount } = {},
    } = leadHealth;

    const title = isQualified ? 'Good Tour Ratio' : 'Bad Tour Ratio';

    const detail = (
      <>
        <SrText font="greycliff" weight="600" fontSize="16px" lineHeight="25px" color="grey">
          {`(${pluralize('tour', toursCount || 0, true)} and ${pluralize(
            'lead',
            leadsCount || 0,
            true,
          )})`}
        </SrText>
        &nbsp;
        <SrTooltip
          iconName={SR_TOOLTIP_ICON_QUESTION_MARK}
          position={SR_TOOLTIP_POSITION_LEFT}
          className="tooltip-healthIndicator"
        >
          Green checkmark if there is at least 1 tour every 5 leads.
        </SrTooltip>
      </>
    );
    return {
      title,
      isQualified,
      detail,
    };
  };

  const _getLeadHealthIndicatorsComponents = () => {
    const leadHealthIndicatorsList = [
      _getProcessingAppsIndicator(),
      _getZillowIndicator(),
      _getActiveLeadsIndicator(),
      _getLeadRatioPast14DaysIndicator(),
      _getLeadRatioIndicator(),
      _getTourRatioIndicator(),
    ];

    return leadHealthIndicatorsList.map((indicator) => (
      <SrQualifiedListItem
        key={indicator.title}
        title={indicator.title}
        isQualified={indicator.isQualified}
        detail={indicator.detail}
        marginTop="15px"
      />
    ));
  };

  const _getArchiveReasonsTitle = () => {
    const {
      leads: { archived: { reasons: { qualified, unqualified, other } = {} } = {} } = {},
    } = leadHealth;

    const totalQualified = qualified.reduce((memo, { count }) => memo + count, 0);
    const totalUnqualified = unqualified.reduce((memo, { count }) => memo + count, 0);

    const qualifiedText = totalQualified > 0 ? `${totalQualified} qualified` : '';

    const unqualifiedText = totalUnqualified > 0 ? `${totalUnqualified} unqualified` : '';

    const otherText = other.length ? `${other[0].count} other` : '';

    if (!qualifiedText && !unqualifiedText && !otherText) {
      return 'Archive reasons';
    }

    return `Archive reasons (${lodashCompact([qualifiedText, unqualifiedText, otherText]).join(
      ', ',
    )})`;
  };

  const _getTotalArchiveReasons = () => {
    const { leads } = leadHealth;

    return leads.archived.count;
  };

  const _getArchiveTagComponent = ({ archiveReasons = [] }) => {
    const archiveReasonsDOM = archiveReasons.map(({ reason, count, highlight }) => {
      const reasonFound = lodashFind({ value: reason }, LEAD_ARCHIVE_ALL_REASONS_OPTION_ITEMS);
      if (!reasonFound?.label) {
        return null;
      }

      return (
        <SrBox key={reason} marginBottom="5px">
          <SrTag
            key={reason}
            theme={highlight ? SR_TAG_THEME_ORANGE_OUTLINE : SR_TAG_THEME_GREY_OUTLINE}
          >
            <SrText font="greycliff" weight="500" fontSize="12px" lineHeight="18px">
              {reasonFound.label} &nbsp;
            </SrText>
            <SrText font="greycliff" weight="700" fontSize="12px" lineHeight="18px">
              ({count})
            </SrText>
          </SrTag>
        </SrBox>
      );
    });

    const highlight = archiveReasons.find(({ highlight }) => highlight);

    return (
      <>
        {highlight && (
          <SrBox marginTop="5px">
            <SrText color="orange300" weight="500" lineHeight="18px" size="12">
              Sunroom highlights feedback in orange which you can act on to reduce vacancy.
            </SrText>
          </SrBox>
        )}
        <SrFlex wrap="wrap" marginTop="12px">
          {archiveReasonsDOM}
        </SrFlex>
      </>
    );
  };

  const _getArchivedReasonsComponents = () => {
    const { leads: { archived } = {} } = leadHealth;

    if (archived.count === 0) {
      return [];
    }

    const allArchiveReasons = [
      ...archived.reasons.qualified,
      ...archived.reasons.unqualified,
      ...archived.reasons.other,
    ].sort((a, b) => (a.count > b.count ? -1 : 1));

    return _getArchiveTagComponent({
      archiveReasons: allArchiveReasons.map((archiveReason) => {
        return {
          ...archiveReason,
          highlight: RENTLINX_LISTING_LEAD_HIGHLIGHT_ARCHIVE_REASON_VALUES.includes(
            archiveReason.reason,
          ),
        };
      }),
    });
  };

  const onCloseNotesModal = async () => {
    if (reloadListingNotes) {
      await reloadListingNotes();
    }
  };

  const _getListingDetails = () => {
    const { listing, partner, market } = leadHealth;

    return (
      <SrFlex direction="col">
        <SrLink to={`/partner/${partner.slug}/listing/${listing.id}`}>
          <SrText font="greycliff" size="16" color="blue300">
            {partner.displayName}
          </SrText>
        </SrLink>
        <SrText font="greycliff" fontSize="21px" weight="800" letterSpacing="0" paddingTop="4px">
          {makeListingAddressStreetDisplay(listing)}
        </SrText>
        {market && (
          <SrText font="greycliff" color="grey" size="12" weight="500">
            {market.displayName}
            <SrText
              font="greycliff"
              color="grey"
              size="10"
              lineHeight="18px"
              weight="500"
              paddingLeft="8px"
            >
              {listing.daysOnMarket} days on market
            </SrText>
          </SrText>
        )}
        <SrFlex justify="space-between" marginTop="8px">
          <SrListingNotesChip
            listingId={listing.id}
            notesCount={listing.notesCount}
            onModalClose={onCloseNotesModal}
          />
        </SrFlex>
        <SrSeparator marginTop="18px" marginBottom="18px" />
      </SrFlex>
    );
  };

  const leadHealthIndicatorsComponents = _getLeadHealthIndicatorsComponents();
  const archiveReasonsTitle = _getArchiveReasonsTitle();
  const totalArchiveReasons = _getTotalArchiveReasons();
  const archivedReasonsComponents = _getArchivedReasonsComponents();
  const listingDetails = showListingDetails ? _getListingDetails() : null;

  return (
    <SrCard>
      {listingDetails}
      <SrBox>
        <SrBox>
          <SrText
            font="greycliff"
            fontSize="21px"
            lineHeight="28px"
            weight="800"
            letterSpacing="0.5"
          >
            Lead Health
          </SrText>
        </SrBox>
        <SrBox marginTop="23px">
          <SrBox>{leadHealthIndicatorsComponents}</SrBox>
        </SrBox>
      </SrBox>
      {totalArchiveReasons > 0 && (
        <SrBox marginTop="23px">
          <SrBox>
            <SrText
              font="greycliff"
              fontSize="18px"
              lineHeight="24px"
              weight="800"
              letterSpacing="0.5"
            >
              {archiveReasonsTitle}
              &nbsp;
              <SrTooltip
                iconName={SR_TOOLTIP_ICON_QUESTION_MARK}
                position={SR_TOOLTIP_POSITION_LEFT}
                className="tooltip-healthIndicator"
              >
                Qualified and unqualified status is defined by the archive reasons. For example
                “Move-in date doesn’t match” is an unqualified lead
              </SrTooltip>
            </SrText>
          </SrBox>
          {archivedReasonsComponents}
        </SrBox>
      )}
    </SrCard>
  );
};

SrLeadHealthCard.displayName = 'SrLeadHealthCard';

export default SrLeadHealthCard;
